import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../lib/analytics';

/**
 * Hook to track page views in Google Analytics
 * Automatically tracks when route changes
 */
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view when location changes
    trackPageView(location.pathname + location.search);
  }, [location]);
};

/**
 * Hook to track scroll depth
 * Reports when user scrolls to 25%, 50%, 75%, and 100% of page
 */
export const useScrollTracking = () => {
  useEffect(() => {
    const reported = {
      25: false,
      50: false,
      75: false,
      100: false
    };

    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrollTop = window.scrollY;
      const scrollPercentage = (scrollTop / documentHeight) * 100;

      // Report scroll depth milestones
      Object.entries(reported).forEach(([milestone, isReported]) => {
        const milestoneNum = parseInt(milestone);
        if (!isReported && scrollPercentage >= milestoneNum) {
          reported[milestoneNum as keyof typeof reported] = true;
          
          // Send event to analytics
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'scroll', {
              percent_scrolled: milestoneNum,
              page_path: window.location.pathname
            });
          }
        }
      });
    };

    // Throttle scroll events
    let scrollTimer: NodeJS.Timeout;
    const throttledScroll = () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(handleScroll, 100);
    };

    window.addEventListener('scroll', throttledScroll);
    
    // Check initial scroll position
    handleScroll();

    return () => {
      window.removeEventListener('scroll', throttledScroll);
      clearTimeout(scrollTimer);
    };
  }, []);
};

/**
 * Hook to track time on page
 * Reports engagement time in 10-second increments
 */
export const useEngagementTracking = () => {
  useEffect(() => {
    let timeOnPage = 0;
    let lastReportedTime = 0;

    const interval = setInterval(() => {
      timeOnPage += 10;

      // Report every 30 seconds
      if (timeOnPage - lastReportedTime >= 30) {
        lastReportedTime = timeOnPage;
        
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'user_engagement', {
            engagement_time_msec: timeOnPage * 1000,
            page_path: window.location.pathname
          });
        }
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, []);
};